import {
    type ComponentProps,
    type ComponentType,
    PureComponent,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import { type Unsubscribe } from "redux";
import { type CamelCase, type Without } from "../../../../types/internal/utility";
import { Application } from "../../../_Bootstrap/Application";
import { useShopfrontSelector } from "../../../_Bootstrap/hooks";
import * as RoutingAndStore from "../../../_Bootstrap/RoutingAndStore";
import { store } from "../../../_Bootstrap/RoutingAndStore";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { NAVIGATION_USER_UPDATE } from "../../../Layout/Navigation/Navigation";
import { CancellablePromise } from "../../Generic/Promises";
import { camelCase } from "../../Generic/Strings";

export type PermissionsType = Record<string, boolean>;

export class Permissions {
    private static _normalisePermissions(permissions: Array<string> | string): Array<string> {
        if(!Array.isArray(permissions)) {
            permissions = [ permissions ];
        }

        return permissions;
    }

    public static checkPermissions(
        permissions: Array<string>,
        userPermissions: null | Record<string, number>
    ): boolean {
        if(!userPermissions) {
            return true;
        }

        for(let i = 0, l = permissions.length; i < l; i++) {
            if(Permissions.PERMISSION_DEPENDENCIES[permissions[i]]) {
                if(!Permissions.checkPermissions(
                    Permissions.PERMISSION_DEPENDENCIES[permissions[i]],
                    userPermissions
                )) {
                    return false;
                }
            }

            if(!(permissions[i] in userPermissions)) {
                return false;
            }
        }

        return true;
    }

    public static async check<
        T extends ReadonlyArray<string>,
        TV extends T[number],
        TR extends { [key in TV as key]: boolean }
    >(permissions: T): Promise<TR> {
        // Prepare results
        const results: Record<string, boolean> = {};

        const store = RoutingAndStore.store.getState();

        if(!(store.user && store.user.id)) {
            // No user so return false for every permission
            for(let i = 0, l = permissions.length; i < l; i++) {
                results[permissions[i]] = false;
            }

            return results as TR;
        }

        if(store.user.isAdmin) {
            // Admin has all permissions
            for(let i = 0, l = permissions.length; i < l; i++) {
                results[permissions[i]] = true;
            }
        } else {
            // Get user
            const userId = store.user.id;
            const dataSource = await determineDataSource();
            const user = await dataSource.users.get(userId) as any;

            // Check all the permissions and prepare results
            for(let i = 0, l = permissions.length; i < l; i++) {
                results[permissions[i]] = Permissions.checkPermissions([ permissions[i] ], user.permissions);
            }
        }

        return results as TR;
    }

    public static async can(permissions: Array<string> | string): Promise<boolean> {
        permissions = Permissions._normalisePermissions(permissions);

        const store = Application.get("Redux").getState();

        if(!(store.user && store.user.id)) {
            return false;
        }

        if(store.user.isAdmin) {
            return true;
        }

        const userId = store.user.id;
        const dataSource = await determineDataSource();

        let user;

        try {
            user = await dataSource.users.get(userId);
        } catch {
            return false;
        }

        return Permissions.checkPermissions(permissions, user.permissions);
    }

    public static async who(permissions: Array<string> | string): Promise<Array<{ id: string; name: string; }>> {
        permissions = Permissions._normalisePermissions(permissions);

        const dataSource = await determineDataSource();
        const users = await dataSource.users.all() as Array<any>;

        const allowed = [];

        for(let i = 0, l = users.length; i < l; i++) {
            if(Permissions.checkPermissions(permissions, users[i])) {
                allowed.push({
                    id  : users[i].uuid,
                    name: users[i].name,
                });
            }
        }

        return allowed;
    }

    public static PERMISSION_DEPENDENCIES: Record<string, Array<string>> = {
        adjust_customer_group        : [ "edit_customers", "see_customer_groups" ],
        adjust_customer_loyalty      : [ "edit_customers", "edit_customer_groups" ],
        adjust_customer_pricing      : [ "edit_customers", "edit_customer_groups" ],
        associate_barcode            : [ "edit_barcodes" ],
        edit_barcodes                : [ "edit_products" ],
        cancel_held_sale             : [ "hold_sale" ],
        cashout                      : [ "sell" ],
        create_classifications       : [ "see_classifications" ],
        create_advance_promotions    : [ "see_promotions" ],
        adjust_customer_group_pricing: [ "adjust_customer_group", "edit_customer_groups" ],
        adjust_customer_group_loyalty: [ "adjust_customer_group", "edit_customer_groups" ],
        create_customer_groups       : [ "see_customer_groups" ],
        create_express_promotions    : [ "see_promotions" ],
        create_media_folder          : [ "see_media_centre" ],
        create_pricelists            : [ "see_pricelists" ],
        create_products              : [ "see_products" ],
        create_suppliers             : [ "see_suppliers" ],
        create_webhooks              : [ "see_webhooks" ],
        delete_media_content         : [ "see_media_centre" ],
        delete_media_folder          : [ "see_media_centre" ],
        delete_pricelists            : [ "see_pricelists" ],
        delete_products              : [ "see_products" ],
        delete_promotions            : [ "see_promotions" ],
        delete_suppliers             : [ "see_suppliers" ],
        delete_webhooks              : [ "see_webhooks" ],
        discount                     : [ "sell" ],
        edit_advance_promotions      : [ "see_promotions" ],
        edit_classifications         : [ "see_classifications" ],
        edit_customer_groups         : [ "see_customer_groups" ],
        edit_customers               : [ "see_customers" ],
        edit_express_promotions      : [ "see_promotions" ],
        edit_pricelists              : [ "see_pricelists" ],
        edit_products                : [ "see_products" ],
        edit_suppliers               : [ "see_suppliers" ],
        edit_webhooks                : [ "see_webhooks" ],
        history_cancel_sale          : [ "history_modify_sale" ],
        history_modify_sale          : [ "see_history" ],
        hold_sale                    : [ "sell" ],
        delete_customer_groups       : [ "see_customer_groups" ],
        perform_stocktake            : [ "see_stocktake" ],
        product_modify_inventory     : [ "edit_products" ],
        refund                       : [ "sell" ],
        rename_media_content         : [ "see_media_centre" ],
        rename_media_folder          : [ "see_media_centre" ],
        run_actions_reports          : [ "see_cost", "view_revision_history" ],
        run_advanced_reports         : [ "see_cost" ],
        delete_classifications       : [ "see_classifications" ],
        run_purchase_reports         : [ "see_cost" ],
        run_sales_reports            : [ "see_cost" ],
        see_balances                 : [ "see_customers" ],
        stock_create_order           : [ "see_stock_management" ],
        run_inventory_reports        : [ "see_cost" ],
        stock_create_transfer        : [ "see_stock_management" ],
        stock_delete                 : [ "see_stock_management" ],
        stock_edit_credit            : [ "see_stock_management" ],
        stock_edit_order             : [ "see_stock_management" ],
        stock_edit_return            : [ "see_stock_management" ],
        stock_create_return          : [ "see_stock_management" ],
        stock_receive                : [ "see_stock_management" ],
        stock_send                   : [ "see_stock_management" ],
        stocktake_apply_ignore       : [ "perform_stocktake" ],
        perform_safe_drop            : [ "see_manage_takings", "manage_takings_adjust_movement" ],
        upload_media_content         : [ "see_media_centre" ],
        stock_edit_transfer          : [ "see_stock_management" ],
        view_product                 : [ "see_products" ],
        view_reporting_dashboard     : [ "run_sales_reports" ],
        delete_customers             : [ "see_customers" ],
        stocktake_apply_zero         : [ "perform_stocktake" ],
        modify_settings              : [
            "modify_sale_keys",
            "modify_receipts",
            "modify_customer_displays",
            "modify_users",
            "modify_statements",
            "see_products",
        ],
        manage_enterprise_connections: [ "view_enterprise_connections" ],
        modify_ticket_templates      : [ "see_shelf_tickets" ],
        create_users                 : [ "modify_roles_permissions" ],
        configure_kitchen_screens    : [ "use_bump_screen" ],
        see_host_changes             : [ "see_host_connections", "see_cost" ],
        modify_host_connections      : [ "see_host_connections" ],
        manage_host_changes          : [ "see_host_changes" ],
        modify_integrations          : [ "integrate_applications" ],
        modify_default_prices        : [ "edit_products" ],
    };
}

export interface InjectedPermissionProps {
    permissions: PermissionsType;
}

interface WithPermissionState extends InjectedPermissionProps {
    permissionUserId: false | string;
}

export function withPermissions<
    C extends ComponentType<ComponentProps<C> & InjectedPermissionProps>,
    WrappedProps = React.JSX.LibraryManagedAttributes<C, Without<ComponentProps<C>, InjectedPermissionProps>>
>(
    WrappedComponent: C,
    permissions: Array<string>,
    defaultPermission = true
): ComponentType<WrappedProps> {
    return class extends PureComponent<WrappedProps, WithPermissionState> {
        public static displayName = "WithPermissions";

        private _subscription: false | Unsubscribe = false;
        private _cancellablePromise?: CancellablePromise<PermissionsType>;

        public defaultPermissions: PermissionsType;

        constructor(props: Readonly<WrappedProps>) {
            super(props);

            const defaultPermissions: PermissionsType = {};

            for(let i = 0, l = permissions.length; i < l; i++) {
                defaultPermissions[permissions[i]] = defaultPermission;
            }

            this.defaultPermissions = defaultPermissions;

            this.state = {
                permissions     : this.defaultPermissions,
                permissionUserId: false,
            };

            this.updateUserId = this.updateUserId.bind(this);
            this.updateUserPermissions = this.updateUserPermissions.bind(this);
        }

        public componentDidMount(): void {
            this.updateUserId();
            this.subscribe();
            void this.updateUserPermissions();
            document.addEventListener(NAVIGATION_USER_UPDATE, this.updateUserPermissions, false);
        }

        public componentDidUpdate(
            prevProps: Readonly<WrappedProps>,
            prevState: Readonly<WithPermissionState>
        ): void {
            if(prevState.permissionUserId !== this.state.permissionUserId) {
                void this.updateUserPermissions();
            }
        }

        public componentWillUnmount(): void {
            this.unsubscribe();
            document.removeEventListener(NAVIGATION_USER_UPDATE, this.updateUserPermissions);

            this._cancellablePromise?.cancel();
        }

        public subscribe(): void {
            this._subscription = store.subscribe(this.updateUserId);
        }

        public unsubscribe(): void {
            if(this._subscription) {
                this._subscription();
            }
        }

        public updateUserId(): void {
            const state = store.getState();

            if(typeof state.user.id === "undefined") {
                if(this.state.permissionUserId !== false) {
                    this.setState({
                        permissionUserId: false,
                    });
                }
            } else if(this.state.permissionUserId !== state.user.id) {
                this.setState({
                    permissionUserId: state.user.id,
                });
            }
        }

        public async getUserPermissions(): Promise<PermissionsType> {
            if(!this.state.permissionUserId) {
                return this.defaultPermissions;
            }

            const source = await determineDataSource();

            let user;

            try {
                user = await source.users.get(this.state.permissionUserId);
            } catch {
                // User was not found, could be Shopfront, offline, or they require syncing
                return this.defaultPermissions;
            }

            const userPermissions: PermissionsType = {};

            for(let i = 0, l = permissions.length; i < l; i++) {
                userPermissions[permissions[i]] = Permissions.checkPermissions([ permissions[i] ], user.permissions);
            }

            return userPermissions;
        }

        public updateUserPermissions(): Promise<void> {
            if(this._cancellablePromise) {
                this._cancellablePromise.cancel();
            }

            this._cancellablePromise = new CancellablePromise(this.getUserPermissions());

            return this._cancellablePromise
                .promise
                .then(permissions => {
                    this.setState({
                        permissions,
                    });
                })
                .catch(err => {
                    if(!err.cancelled) {
                        console.error("Failed to get user permissions", err);
                    }
                });
        }

        public render(): ReactNode {
            return (
                <WrappedComponent
                    {...this.props as any}
                    permissions={this.state.permissions}
                />
            );
        }
    };
}

/**
 * Hook to retrieve the current permissions, it's highly suggested to use a "const" array from outside the rendering
 * function
 * @example
 * const permssions = ["edit_customers"] as const;
 *
 * const FunctionComponent = () => {
 *      const {
 *          editCustomers
 *      } = usePermissions(permissions);
 * };
 */
export const usePermissions = <
    T extends ReadonlyArray<string>,
    TV extends T[number],
    TR extends { [key in TV as CamelCase<key>]: boolean }
>(permissions: T, defaultPermission = true): TR => {
    const user = useShopfrontSelector(state => state.user.id);
    const defaultPermissions = useMemo(() => {
        const defaultPermissions = {} as Record<string, boolean>;

        for(let i = 0, l = permissions.length; i < l; i++) {
            defaultPermissions[camelCase(permissions[i])] = defaultPermission;
        }

        return defaultPermissions;
    }, [ defaultPermission, permissions ]);
    const [ allowedPermissions, setAllowedPermissions ] = useState<Record<string, boolean>>(defaultPermissions);

    useEffect(() => {
        if(!user) {
            setAllowedPermissions(defaultPermissions);

            return;
        }

        determineDataSource()
            .then(source => source.users.get(user))
            .then(user => {
                const userPermissions = {} as Record<string, boolean>;

                for(let i = 0, l = permissions.length; i < l; i++) {
                    userPermissions[camelCase(permissions[i])] = Permissions.checkPermissions(
                        [ permissions[i] ],
                        user.permissions
                    );
                }

                setAllowedPermissions(userPermissions);
            });
    }, [ user, defaultPermissions, permissions ]);

    return allowedPermissions as TR;
};
